import { produce } from 'immer';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useBreakpoints from '../../../../common/hooks/useBreakpoints';
import Breadcrumbs from '../../../../common/widgets/Breadcrumbs';
import Icons from '../../../../common/widgets/Icons';
import {
  getCategoryFallbackPath,
  onlineFilter,
  TaxPayer,
} from '../../constants';
import { BenefitsPageProps } from '../../contexts/common';
import {
  BuyerTypes,
  DyPageTypeEnum,
  FilterTypes,
  RevolutionSliderTypes,
  TopLevelCategories,
} from '../../enums';
import { CategoryDetails } from '../../models/Categories';
import { ErrorType } from '../../models/Errors';
import { TaxFilter } from '../../models/Filters';
import { PriceRange } from '../../models/PriceRange';
import { resources } from '../../resources/resources';
import { categoryService } from '../../services/category';
import { dynamicYieldService } from '../../services/dynamic-yield';
import { filterService } from '../../services/filters';
import { productsService } from '../../services/products';
import { SmartNotifications } from '../../widgets/SmartNotifications';
import applicationCrumbs from '../ApplicationCrumbs';
import { getCategoryBreadcrumbs } from '../BenefitsPage/utils';
import CategoriesSideMenu from '../Common/CategoriesSideMenu';
import FilterPanel from '../Common/Filters';
import MktSlideshow from '../Common/Marketing/MktSlideshow';
import RecommendedProducts from '../Common/RecommendedProducts';
import MobileProductsComponent from '../ProductsPage/ProductsSearched/MobileSearch/MobileSearchResults';
import TutorialsPanel from '../TutorialsPanel';
import BenefitBucksWallet from './Components/BenefitBucks';
import MostPopularBrands from './Components/MostPopularBrands';
import ProductListPanel from './Components/ProductsListPanel';
import { queryParamKeys } from './constants';
import {
  AvailableInFilterState,
  BrandFilterState,
  BuyerTypeFilterState,
  filterSelectionDefaults,
  FilterSelectionsState,
  PriceRangeFilterState,
  ProductListState,
  ProductsOnOfferFilterState,
  TaxationLevelFilterState,
} from './types';
import {
  clearFilterQueryParams,
  getCurentCategory,
  getDiscountedProductsCount,
  getFiltredProducts,
  getFiltredSelectionState,
  getPriceRange,
  getSortedProduts,
  getUpdatedFilters,
  hasSelectedFilters,
  updateArrayQueryParam,
  updateBooleanQueryParam,
  updatePriceRangeQueryParam,
  updateSortQueryParam,
} from './utils';

function CategoryNotifications(props: any) {
  const { categoryId } = props;

  return (
    <div className="toast-wrapper-bottom">
      <div>
        <SmartNotifications
          productId={null}
          categoryId={Number(categoryId)}
          isForHome={false}
        />
      </div>
    </div>
  );
}

function CategorySlideshow(props: any) {
  const { topLevelCategoryId } = props;

  if (topLevelCategoryId === TopLevelCategories.FlexibleBenefits) {
    return (
      <MktSlideshow
        sliderType={RevolutionSliderTypes.FlexibleBenefitsSlideshow}
      />
    );
  }
  if (
    topLevelCategoryId === TopLevelCategories.BenefitClub ||
    topLevelCategoryId === TopLevelCategories.Cashback
  ) {
    return (
      <MktSlideshow sliderType={RevolutionSliderTypes.BenefitClubSlideshow} />
    );
  }
  return null;
}

const CASHBACK_CATEGORY_DATA = {
  topLevelCategoryId: TopLevelCategories.Cashback,
  categoryId: TopLevelCategories.Cashback,
};

export default function CategoryPage(props: Readonly<BenefitsPageProps>) {
  const { topLevelCategoryId, forcedCategoryId } = props;
  const params = useParams<{ categoryId?: string }>();
  const categoryId = forcedCategoryId || params.categoryId;
  const { t: translate } = useTranslation();
  const { isTablet } = useBreakpoints();
  const prevCategoryIdRef = useRef('');

  const buyerTypeList = useMemo(
    () => [
      {
        buyerType: BuyerTypes.Employee,
        name: translate(resources.filters.buyerType.employee),
      },
      {
        buyerType: BuyerTypes.Dependent,
        name: translate(resources.filters.buyerType.dependent),
      },
    ],
    [translate],
  );

  const crumbs = applicationCrumbs(translate);
  const ref = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const brandId = searchParams.get(queryParamKeys.brand);

  const sortIdParam = searchParams.get(queryParamKeys.sortId);
  const sortId = sortIdParam ? Number(sortIdParam) : undefined;

  const skipParam = searchParams.get(queryParamKeys.skip);
  const skip = skipParam ? Number(skipParam) : undefined;

  const takeParam = searchParams.get(queryParamKeys.take);
  const take = takeParam ? Number(takeParam) : undefined;

  // #region state
  const [currentCategoryName, setCurrentCategoryName] = useState<string>(
    topLevelCategoryId === TopLevelCategories.Cashback
      ? translate(resources.menu.cashback)
      : '',
  );
  const [currentCategoryDetails, setCurrentCategoryDetails] = useState<
    CategoryDetails | undefined
  >();
  const [productState, setProductState] = useState<ProductListState>({
    productList: undefined,
    filtredProductList: undefined,
  });
  const [availableInFilterState, setAvailableInFilterState] =
    useState<AvailableInFilterState>({
      availableInList: undefined,
      filteredAvailableInList: undefined,
    });
  const [brandFilterState, setBrandFilterState] = useState<BrandFilterState>({
    brandList: undefined,
    filteredBrandList: undefined,
  });
  const [buyerTypeFilterState, setBuyerTypeFilterState] =
    useState<BuyerTypeFilterState>({
      buyerTypeList: undefined,
      filteredBuyerTypeList: undefined,
    });
  const [taxationLevelFilterState, setTaxationLevelFilterState] =
    useState<TaxationLevelFilterState>({
      taxationLevelList: undefined,
      filteredTaxationLevelList: undefined,
    });
  const [
    employerTaxationLevelFilterState,
    setEmployerTaxationLevelFilterState,
  ] = useState<TaxationLevelFilterState>({
    taxationLevelList: undefined,
    filteredTaxationLevelList: undefined,
  });
  const [priceRangeFilterState, setPriceRangeFilterState] =
    useState<PriceRangeFilterState>({
      priceRange: undefined,
    });

  const [productsOnOfferFilterState, setProductsOnOfferFilterState] =
    useState<ProductsOnOfferFilterState>({
      discountedProductsCount: 0,
    });

  const [filterSelectionState, setFilterSelectionState] =
    useState<FilterSelectionsState>(getFiltredSelectionState(searchParams));
  const [currentFilterType, setCurrentFilterType] = useState<
    number | undefined
  >();
  // #endregion

  const productsFilteredByBrand = useMemo(
    () =>
      brandId
        ? productState.productList?.filter(p => p.brandId === Number(brandId))
        : productState.productList,
    [brandId, productState.productList],
  );

  const hasFilters = brandId
    ? productsFilteredByBrand?.length !==
      productState.filtredProductList?.length
    : productState.productList?.length !==
      productState.filtredProductList?.length;

  const pageTitle = props.rootCategory
    ? getCurentCategory(props.rootCategory, categoryId)?.name
    : undefined;

  // #region data loader
  const loadProducts = async () => {
    setProductState({
      productList: undefined,
      filtredProductList: undefined,
    });
    setProductsOnOfferFilterState({ discountedProductsCount: 0 });
    setPriceRangeFilterState({ priceRange: undefined });
    setBuyerTypeFilterState({ buyerTypeList: undefined });

    if (!/^\d+$/.test(categoryId || '')) {
      navigate('/not-found');
    }

    const result = await productsService.getProductsForCategory(
      Number(categoryId),
    );

    if (!result.hasErrors) {
      if (result.data.length > 0) {
        const allProducts = result.data;
        const filteredProducts = getFiltredProducts(
          allProducts,
          filterSelectionState,
        );
        const sortedAndFilteredProducts = sortId
          ? getSortedProduts[sortId](filteredProducts)
          : filteredProducts;

        setProductState({
          productList: allProducts,
          filtredProductList: sortedAndFilteredProducts,
        });
        setProductsOnOfferFilterState({
          discountedProductsCount: getDiscountedProductsCount(result.data),
        });
        setPriceRangeFilterState({ priceRange: getPriceRange(result.data) });
        setBuyerTypeFilterState({ buyerTypeList });
      } else {
        navigate('/not-found');
      }
    }
  };

  const loadAvailableInList = async () => {
    setAvailableInFilterState({
      availableInList: undefined,
      filteredAvailableInList: undefined,
    });
    const result = await filterService.getCountyFilterForCategory(
      Number(categoryId),
    );
    if (!result.hasErrors) {
      setAvailableInFilterState({
        availableInList: result.data,
        filteredAvailableInList: result.data,
      });
    }
  };

  const loadTaxationLevelList = async () => {
    setTaxationLevelFilterState({
      taxationLevelList: undefined,
      filteredTaxationLevelList: undefined,
    });
    const result = await filterService.getTaxFilterForCategory(
      Number(categoryId),
    );
    if (!result.hasErrors) {
      const employeeTaxationLevels = result.data.filter(
        e => e.payer === TaxPayer.Employee,
      );
      const employerTaxationLevels = result.data.filter(
        e => e.payer === TaxPayer.Employer,
      );
      setTaxationLevelFilterState({
        taxationLevelList: employeeTaxationLevels,
        filteredTaxationLevelList: employeeTaxationLevels,
      });
      setEmployerTaxationLevelFilterState({
        taxationLevelList: employerTaxationLevels,
        filteredTaxationLevelList: employerTaxationLevels,
      });
    }
  };

  const loadBrandList = async () => {
    setBrandFilterState({
      brandList: undefined,
      filteredBrandList: undefined,
    });
    const result = await filterService.getBrandFilterForCategory(
      Number(categoryId),
    );
    if (!result.hasErrors) {
      setBrandFilterState({
        brandList: result.data,
        filteredBrandList: result.data,
      });
    }
  };

  const loadCategoryDetails = async (): Promise<boolean> => {
    setCurrentCategoryDetails(undefined);

    const result = await categoryService.getCategoryDetails(Number(categoryId));

    if (result.hasErrors) {
      if (result.errorType === ErrorType.NotFoundError) {
        navigate('/not-found', { replace: true });
      }

      return false;
    }

    if (result.data.shouldFallback) {
      navigate(getCategoryFallbackPath(), { replace: true });
      return false;
    }

    setCurrentCategoryDetails({ ...result.data });
    return true;
  };

  // #endregion

  // #region filter change handlers
  const handlePriceRangeFilterChanged = useCallback(
    (range: PriceRange) => {
      const reportToDynamicYield = () => {
        const minPriceChanged =
          _.isNil(filterSelectionState.priceRange?.min) ||
          filterSelectionState.priceRange?.min !== range.min;
        const maxPriceChanged =
          _.isNil(filterSelectionState.priceRange?.max) ||
          filterSelectionState.priceRange?.max !== range.max;

        if (minPriceChanged) {
          dynamicYieldService.eventFilter(
            FilterTypes.PriceRange,
            range.min,
            null,
          );
        }
        if (maxPriceChanged) {
          dynamicYieldService.eventFilter(
            FilterTypes.PriceRange,
            range.max,
            null,
          );
        }
      };

      reportToDynamicYield();

      updatePriceRangeQueryParam(range);

      setFilterSelectionState(
        produce(draft => {
          draft.priceRange = range;
        }),
      );
      setCurrentFilterType(FilterTypes.PriceRange);
    },
    [filterSelectionState.priceRange],
  );

  const handleAvailableInFilterChanged = useCallback(
    (countyIds: number[]) => {
      const reportToDynamicYield = () => {
        const newCountyId = _.without(
          countyIds,
          ...filterSelectionState.selectedCountyIds,
        )?.[0];
        if (!newCountyId) return;

        const countyName = availableInFilterState.availableInList?.find(
          e => e.filterId === newCountyId,
        )?.description;
        if (!countyName) return;

        dynamicYieldService.eventFilter(
          FilterTypes.AvailableIn,
          null,
          countyName,
        );
      };

      reportToDynamicYield();

      updateArrayQueryParam(queryParamKeys.countyIdFilter, countyIds);

      setFilterSelectionState(
        produce(draft => {
          draft.selectedCountyIds =
            countyIds.length === 0 ? countyIds : [...countyIds, onlineFilter];
        }),
      );
      setCurrentFilterType(FilterTypes.AvailableIn);
    },
    [
      filterSelectionState.selectedCountyIds,
      availableInFilterState.availableInList,
    ],
  );

  const handleBrandFilterChanged = useCallback(
    (brandIds: number[]) => {
      const reportToDynamicYield = () => {
        const newBrandId = _.without(
          brandIds,
          ...filterSelectionState.selectedBrandIds,
        )?.[0];
        if (!newBrandId) return;

        const brandName = brandFilterState.brandList?.find(
          e => e.brandId === newBrandId,
        )?.brandName;
        if (!brandName) return;

        dynamicYieldService.eventFilter(FilterTypes.Brand, null, brandName);
      };

      reportToDynamicYield();

      updateArrayQueryParam(queryParamKeys.brandIdFilter, brandIds);

      setFilterSelectionState(
        produce(draft => {
          draft.selectedBrandIds = brandIds;
        }),
      );
      setCurrentFilterType(FilterTypes.Brand);
    },
    [filterSelectionState.selectedBrandIds, brandFilterState.brandList],
  );

  const handleBuyerTypeFilterChanged = useCallback(
    (buyerTypes: BuyerTypes[]) => {
      updateArrayQueryParam(queryParamKeys.buyerTypeIdFilter, buyerTypes);

      setFilterSelectionState(
        produce(draft => {
          draft.selectedBuyerTypes = buyerTypes;
        }),
      );
      setCurrentFilterType(FilterTypes.BuyerType);
    },
    [],
  );

  const handleTaxFilterChanged = useCallback(
    (taxClassIds: number[]) => {
      const reportToDynamicYield = () => {
        const newTaxClassId = _.without(
          taxClassIds,
          ...filterSelectionState.selectedTaxClassIds,
        )?.[0];
        if (!newTaxClassId) return;

        const rate = taxationLevelFilterState.taxationLevelList?.find(
          e => e.taxClassId === newTaxClassId,
        )?.rate;
        if (_.isNil(rate)) return;

        dynamicYieldService.eventFilter(
          FilterTypes.TaxationLevel,
          null,
          `${rate}%`,
        );
      };

      reportToDynamicYield();

      updateArrayQueryParam(queryParamKeys.taxClassIdFilter, taxClassIds);

      setFilterSelectionState(
        produce(draft => {
          draft.selectedTaxClassIds = taxClassIds;
        }),
      );
      setCurrentFilterType(FilterTypes.TaxationLevel);
    },
    [
      filterSelectionState.selectedTaxClassIds,
      taxationLevelFilterState.taxationLevelList,
    ],
  );

  const handleEmployerTaxFilterChanged = useCallback(
    (taxClassIds: number[]) => {
      const reportToDynamicYield = () => {
        const newTaxClassId = _.without(
          taxClassIds,
          ...filterSelectionState.selectedEmployerTaxClassIds,
        )?.[0];
        if (!newTaxClassId) return;

        const rate = employerTaxationLevelFilterState.taxationLevelList?.find(
          e => e.taxClassId === newTaxClassId,
        )?.rate;
        if (_.isNil(rate)) return;

        dynamicYieldService.eventFilter(
          FilterTypes.TaxationLevel,
          null,
          `${rate}%`,
        );
      };

      reportToDynamicYield();

      updateArrayQueryParam(queryParamKeys.taxClassIdFilter, taxClassIds);

      setFilterSelectionState(
        produce(draft => {
          draft.selectedEmployerTaxClassIds = taxClassIds;
        }),
      );

      setCurrentFilterType(FilterTypes.EmployerTaxationLevel);
    },
    [
      filterSelectionState.selectedEmployerTaxClassIds,
      employerTaxationLevelFilterState.taxationLevelList,
    ],
  );

  const handleProductsOnOfferFilterChanged = useCallback(
    (isSelected: boolean) => {
      const reportToDynamicYield = () => {
        dynamicYieldService.eventFilter(
          FilterTypes.ShowDiscountedProducts,
          null,
          `${isSelected}`,
        );
      };

      reportToDynamicYield();

      updateBooleanQueryParam(queryParamKeys.showDiscountedFilter, isSelected);

      setFilterSelectionState(
        produce(draft => {
          draft.showDiscountedProducts = isSelected;
        }),
      );
      setCurrentFilterType(FilterTypes.ShowDiscountedProducts);
    },
    [],
  );

  const handleSortingOrderChanged = (id: number) => {
    updateSortQueryParam(id);
    setProductState(
      produce(draft => {
        draft.filtredProductList = getSortedProduts[id](
          draft.filtredProductList,
        );
      }),
    );
  };

  const handleCategoryChanged = (categoryName: string) => {
    setCurrentCategoryName(categoryName);
  };

  const handleClearFilters = () => {
    setFilterSelectionState(filterSelectionDefaults);
    clearFilterQueryParams();

    if (brandId) {
      if (productsFilteredByBrand) {
        setPriceRangeFilterState({
          priceRange: getPriceRange(productsFilteredByBrand),
        });
      }
    } else {
      if (productState.productList) {
        setPriceRangeFilterState({
          priceRange: getPriceRange(productState.productList),
        });
      }
    }

    setProductState(
      produce(d => {
        d.filtredProductList = brandId
          ? productsFilteredByBrand
          : d.productList;
      }),
    );
  };

  //#endregion

  useEffect(() => {
    const loadPage = async () => {
      const shouldContinue = await loadCategoryDetails();

      if (!shouldContinue) {
        return;
      }

      loadProducts();
      loadAvailableInList();
      loadBrandList();
      loadTaxationLevelList();
    };

    loadPage();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  useEffect(() => {
    setProductState(
      produce(d => {
        d.filtredProductList = getFiltredProducts(
          brandId ? productsFilteredByBrand : d.productList,
          filterSelectionState,
        );
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterSelectionState.selectedBrandIds,
    filterSelectionState.selectedBuyerTypes,
    filterSelectionState.selectedCountyIds,
    filterSelectionState.selectedTaxClassIds,
    filterSelectionState.selectedEmployerTaxClassIds,
    filterSelectionState.showDiscountedProducts,
    filterSelectionState.priceRange,
  ]);

  useEffect(() => {
    const filters = getUpdatedFilters(
      productState.productList,
      productState.filtredProductList,
      availableInFilterState.availableInList,
      brandFilterState.brandList,
      taxationLevelFilterState.taxationLevelList,
      employerTaxationLevelFilterState.taxationLevelList,
      buyerTypeFilterState.buyerTypeList,
      undefined,
      currentFilterType === FilterTypes.PriceRange,
      hasSelectedFilters(filterSelectionState),
    );

    if (!filters) {
      return;
    }

    if (
      currentFilterType !== FilterTypes.AvailableIn ||
      !filterSelectionState.selectedCountyIds.length
    ) {
      setAvailableInFilterState(
        produce(d => {
          d.filteredAvailableInList = filters.filteredCounties;
        }),
      );
    }

    if (
      currentFilterType !== FilterTypes.TaxationLevel ||
      !filterSelectionState.selectedTaxClassIds.length
    ) {
      setTaxationLevelFilterState(
        produce(d => {
          d.filteredTaxationLevelList = filters.filteredTax;
        }),
      );
    }
    if (
      currentFilterType !== FilterTypes.EmployerTaxationLevel ||
      !filterSelectionState.selectedEmployerTaxClassIds.length
    ) {
      setEmployerTaxationLevelFilterState(
        produce(d => {
          d.filteredTaxationLevelList = filters.filteredEmployerTax;
        }),
      );
    }

    if (
      currentFilterType !== FilterTypes.Brand ||
      !filterSelectionState.selectedBrandIds.length
    ) {
      setBrandFilterState(
        produce(d => {
          d.filteredBrandList = filters.filteredBrands;
        }),
      );
    }

    if (
      currentFilterType !== FilterTypes.BuyerType ||
      !filterSelectionState.selectedBuyerTypes.length
    ) {
      setBuyerTypeFilterState(
        produce(d => {
          d.filteredBuyerTypeList = filters.filteredBuyerTypes;
        }),
      );
    }

    if (currentFilterType !== FilterTypes.PriceRange) {
      setPriceRangeFilterState({ priceRange: filters.priceRange });
    }

    if (currentFilterType !== FilterTypes.ShowDiscountedProducts) {
      setProductsOnOfferFilterState({
        discountedProductsCount: filters.discountedProductsCount,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productState.filtredProductList]);

  useEffect(() => {
    if (!productState.filtredProductList && productState.productList) {
      setProductState(
        produce(d => {
          d.filtredProductList = brandId
            ? productsFilteredByBrand
            : d.productList;
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productState.filtredProductList]);

  useEffect(() => {
    if (brandId) {
      setFilterSelectionState({
        ...getFiltredSelectionState(searchParams),
        selectedBrandIds: [Number(brandId)],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, productState.productList]);

  useEffect(() => {
    if (brandId && productsFilteredByBrand) {
      setPriceRangeFilterState({
        priceRange: getPriceRange(productsFilteredByBrand),
      });
    } else {
      productState.productList &&
        setPriceRangeFilterState({
          priceRange: getPriceRange(productState.productList),
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  useEffect(() => {
    if (!categoryId || !Number.parseInt(categoryId)) {
      return;
    }
    if (
      !prevCategoryIdRef.current ||
      Number.parseInt(prevCategoryIdRef.current) !== Number.parseInt(categoryId)
    ) {
      prevCategoryIdRef.current = categoryId;

      dynamicYieldService.pageviewCategory(
        topLevelCategoryId,
        Number.parseInt(categoryId),
      );
    }
  }, [topLevelCategoryId, categoryId]);

  const commonProps = {
    isDiscountVoucherList:
      currentCategoryDetails?.isVoucherDiscountCategory ?? false,
    isBenefitBucksList: currentCategoryDetails?.showBenefitBucksWallet ?? false,
    showBrands: !brandId,
    initialSortId: sortId,
    initialSkip: skip,
    initialTake: take,
  };

  const filterProps = {
    availableInList: availableInFilterState.filteredAvailableInList,
    brandList: brandFilterState.filteredBrandList,
    buyerTypeList: buyerTypeFilterState.filteredBuyerTypeList,
    taxationLevelList: currentCategoryDetails?.isVoucherDiscountCategory
      ? ([] as TaxFilter[])
      : taxationLevelFilterState.filteredTaxationLevelList,
    employerTaxationLevelList: currentCategoryDetails?.isVoucherDiscountCategory
      ? ([] as TaxFilter[])
      : employerTaxationLevelFilterState.filteredTaxationLevelList,
    priceRange: priceRangeFilterState.priceRange,
    discountedProdutsCount: productsOnOfferFilterState.discountedProductsCount,
    prevSelectionState: filterSelectionState,
    hasActiveFilters: hasFilters,
    handlePriceRangeFilterChanged,
    handleAvailableInFilterChanged,
    handleBrandFilterChanged,
    handleBuyerTypeFilterChanged,
    handleTaxFilterChanged,
    handleEmployerTaxFilterChanged,
    handleProductsOnOfferFilterChanged,
    handleClearFilters,
    filteredProducts: productState.filtredProductList,
  };

  const tutorialsPanelProps = {
    isVisible: false,
    currentRoute: undefined,
    categoryId,
    forMobile: isTablet,
  };

  return (
    <>
      <Breadcrumbs
        entries={[
          ...getCategoryBreadcrumbs(
            crumbs,
            topLevelCategoryId,
            props.rootCategory,
            categoryId,
            brandId
              ? productState.filtredProductList?.find(
                  p => p.brandId === Number(brandId),
                )
              : undefined,
          ),
        ]}
      />
      {!isTablet ? (
        <>
          <section className="section-main section-actions category-page">
            <div className="container" ref={ref}>
              <div className="area-actions">
                {props.rootCategory?.children?.length !== 0 && (
                  <CategoriesSideMenu
                    rootCategory={props.rootCategory}
                    handleCategoryChanged={handleCategoryChanged}
                    forcedCategoryId={categoryId}
                  />
                )}
                <FilterPanel {...filterProps} />
              </div>
              <div className="area-content">
                <CategorySlideshow topLevelCategoryId={topLevelCategoryId} />
                <h1>{currentCategoryName}</h1>
                {topLevelCategoryId === TopLevelCategories.Cashback && (
                  <BenefitBucksWallet />
                )}
                {currentCategoryDetails?.isVoucherDiscountCategory && (
                  <MostPopularBrands categoryId={Number(categoryId)} />
                )}
                <ProductListPanel
                  productList={productState.filtredProductList}
                  forcedCategoryId={categoryId}
                  handleSortingOrderChanged={handleSortingOrderChanged}
                  {...commonProps}
                />
                {categoryId &&
                  Number(categoryId) === TopLevelCategories.Cashback && (
                    <RecommendedProducts
                      pageType={DyPageTypeEnum.Category}
                      categoryPageData={CASHBACK_CATEGORY_DATA}
                    />
                  )}

                {!!currentCategoryDetails?.description.length && (
                  <div
                    id="category-description"
                    className="category-description"
                  >
                    <Icons.Info />
                    <div
                      dangerouslySetInnerHTML={{
                        __html: currentCategoryDetails?.description || '<></>',
                      }}
                    ></div>
                  </div>
                )}
              </div>
            </div>
          </section>
        </>
      ) : (
        <MobileProductsComponent
          filteredProducts={productState.filtredProductList}
          handleSortingOrderChange={handleSortingOrderChanged}
          forcedCategoryId={categoryId}
          filters={{
            filteredAvailableInList:
              availableInFilterState.filteredAvailableInList,
            filteredBrandList: brandFilterState.filteredBrandList,
            filteredBuyerTypeList: buyerTypeFilterState.filteredBuyerTypeList,
            filteredTaxationLevelList:
              currentCategoryDetails?.isVoucherDiscountCategory
                ? ([] as TaxFilter[])
                : taxationLevelFilterState.filteredTaxationLevelList,
            filteredEmployerTaxationLevelList:
              currentCategoryDetails?.isVoucherDiscountCategory
                ? ([] as TaxFilter[])
                : employerTaxationLevelFilterState.filteredTaxationLevelList,
            priceRange: priceRangeFilterState.priceRange,
            discountedProductsCount:
              productsOnOfferFilterState.discountedProductsCount,
            selectionFilterState: filterSelectionState,
            hasActiveFilters: hasFilters,
            priceRangeFilterChanged: handlePriceRangeFilterChanged,
            availableInFilterChanged: handleAvailableInFilterChanged,
            brandFilterChanged: handleBrandFilterChanged,
            buyerTypeFilterChanged: handleBuyerTypeFilterChanged,
            taxFilterChanged: handleTaxFilterChanged,
            employerTaxFilterChanged: handleEmployerTaxFilterChanged,
            productsOnOfferFilterChanged: handleProductsOnOfferFilterChanged,
            clearFilters: handleClearFilters,
          }}
          isTabletView={isTablet}
          title={pageTitle}
          categoryDetails={currentCategoryDetails}
          {...commonProps}
        />
      )}
      <TutorialsPanel {...tutorialsPanelProps} />

      <CategoryNotifications categoryId={categoryId} />
    </>
  );
}
